import type { ProductSummary } from "../api/catalog";
import { productUnit } from "../lib/productUnit";

export { isPieces } from "../lib/productUnit";

/** A line in the offer editor, before it is saved. */
export type OfferEditLine = {
  productId: string | null;
  sku: string | null;
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number | null;
  discountPct: number;
  /** The line's VAT rate: its article's; null for a line without an article, which takes the general rate. */
  vatPct: number | null;
};

/**
 * The general VAT rate, for what has no article to take a rate from: a line without one, an article the ERP gave no
 * rate, and the transport the customer pays. VAT is set per article, never per offer. Mirrors the server.
 */
export const GENERAL_VAT = 20;

/** A Serbian customer: quoted in dinars and charged VAT. */
export function isDomestic(country: string | null | undefined): boolean {
  return (country ?? "").trim().toUpperCase() === "RS";
}

/**
 * The currency an offer is written in: a domestic (Serbian) customer is quoted in dinars,
 * every other customer in euros. Mirrors the server, which decides it when the offer is created;
 * an offer that already exists keeps the currency it was saved with.
 */
export function offerCurrencyForCountry(country: string | null | undefined): "RSD" | "EUR" {
  return isDomestic(country) ? "RSD" : "EUR";
}

/**
 * The line a catalog product adds to an offer. It carries the article's own unit, and the catalog
 * price only when that price is in the offer's currency: a dinar price written into a euro offer
 * would read as euros. Without such a price the field stays empty and the server may still fill
 * it from the ERP list in the offer's currency.
 */
export function offerLineFromProduct(p: ProductSummary, offerCurrency: string): OfferEditLine {
  const sameCurrency = p.listPrice != null && (p.currency ?? "").toUpperCase() === offerCurrency.toUpperCase();
  return {
    productId: p.id,
    sku: p.sku,
    name: p.name ?? p.sku,
    quantity: 1,
    unit: productUnit(p, "PCS"),
    unitPrice: sameCurrency ? p.listPrice : null,
    discountPct: 0,
    vatPct: p.vatRate ?? null,
  };
}

/**
 * A contract's agreed price in the offer's currency. Domestic trade is in dinars by law, so a dinar offer under a
 * contract in euros (or another currency) quotes the price at the NBS middle rate, to the para; without the rate the
 * price is left for the rep to type. An offer in the contract's own currency takes the price as it is.
 */
export function contractPriceIn(
  price: number | null,
  contractCurrency: string | null,
  offerCurrency: string,
  dinarsPerUnit: number | null,
): number | null {
  if (price == null || offerCurrency !== "RSD" || !contractCurrency || contractCurrency === "RSD") return price;
  return dinarsPerUnit == null ? null : Math.round(price * dinarsPerUnit * 100) / 100;
}

/**
 * VAT of an offer the way the server and the ERP count it: each line at its own rate (a line without one at the
 * general rate), rounded once on the sum. Also the rates in use, for the label beside the total.
 */
export function offerVat(lines: { net: number; vatPct: number | null }[]) {
  const rates = lines.map((l) => l.vatPct ?? GENERAL_VAT);
  const amount = Math.round(lines.reduce((s, l, i) => s + l.net * (rates[i] / 100), 0) * 100) / 100;
  return { amount, rates: [...new Set(rates)].sort((a, b) => a - b) };
}
